import type { Request, Response } from 'express';
import { ReactElement, useMemo } from 'react';
import { handleMenuData } from '../../bff/handleMenuData';
import { TrackPageViewContainer } from '../../common/containers/TrackPageViewContainer';
import { getDehydratedState } from '../../common/getDehydrateState';
import { TabPositionSticky } from '../../common/presentational/TabPositionSticky';
import { redirectInInitialProps } from '../../common/redirectInInitialProps';
import { replaceWhiteSpaceToUnderbar } from '../../common/replaceWhiteSpaceToUnderbar.function';
import { requestContext } from '../../common/requestContext';
import { twMerge } from '../../common/twMerge';
import useTypedSelector from '../../common/useTypedSelector';
import { Loading } from '../../design-system/atoms/Loading/Loading';
import { Layout } from '../../design-system/layout/Layout';
import { SubTabItemType } from '../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import { TasteTab } from '../../design-system/organisms/TasteTab/TasteTab';
import PageLayout from '../../domain/landing/presentational';
import { BOTTOM_LINE_STATIC_LAYOUT_TYPE_LIST, GNB_MENU_UID_MAP } from '../../domain/menu/constant';
import { getMenuFromStore } from '../../domain/menu/util/getMenuFromStore';
import { addError } from '../../errors/ErrorSlice';
import { PagewebErrorType } from '../../errors/PagewebErrorType.enum';
import { thunkSerializeError } from '../../errors/thunkSerializeError.function';
import { Screen, SubtabMenu } from '../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../header/constants';
import { LOG_CLICK_EVENT } from '../../logging/logEvent';
import { PageViewCustomPropsDataKey } from '../../logging/PageViewCustomPropsDataKey.enum';
import { nextReduxWrapper } from '../../state/store';
interface MenuProps {
  menuUid: number;
  screenUid: number;
}
function Menu({
  menuUid,
  screenUid
}: MenuProps) {
  const gnbMenuUidMap = GNB_MENU_UID_MAP;
  const {
    subtabMap,
    subtabMenuMap,
    selectedSubtabMenu,
    layoutId,
    selectedLandingType
  } = useTypedSelector(state => {
    const subtabMap = state.menu.entities.subtabMap;
    const subtabMenuMap = state.menu.entities.subtabMenuMap;
    const selectedSubtabMenu = subtabMenuMap[menuUid];
    const selectedSubtab = screenUid !== undefined ? subtabMap[screenUid] : undefined;
    const layoutId = selectedSubtab ? state.pagewebLayout.layoutIdAndScreenUidMap[selectedSubtab.uid] : undefined;
    const selectedLandingType = selectedSubtab ? state.menu.entities.subtabMap[selectedSubtab.uid].landingType : undefined;
    return {
      subtabMap,
      subtabMenuMap,
      selectedSubtabMenu,
      layoutId,
      selectedLandingType
    };
  });
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const subtabMenuItems: SubTabItemType[] = useMemo(() => {
    const nextMenuUid = subtabMenuMap?.[menuUid]?.uid;
    return selectedSubtabMenu?.screenList.map(screen => ({
      name: screen.title,
      param: String(screen.uid),
      href: `/menu/${nextMenuUid}/screen/${screen.uid}`,
      ageGrade: screen.ageGrade,
      icon: screen.icon
    }), []) || [];
  }, [menuUid, selectedSubtabMenu?.screenList, subtabMenuMap]);
  const subtabTitle = subtabMap?.[screenUid ?? -1]?.title || '';
  return <div className="flex flex-col w-full grow">
      {!!selectedSubtabMenu && screenUid !== undefined && <TabPositionSticky depth={2} isMobile={isMobile} isWebView={isWebView}>
          <TasteTab key={menuUid} tabItems={subtabMenuItems} currentTabItemParam={String(screenUid)} isMobile={isMobile} hasBottomLine={isMobile && selectedLandingType ? BOTTOM_LINE_STATIC_LAYOUT_TYPE_LIST.includes(selectedLandingType) : false} className="before:absolute before:bottom-0 before:left-0 before:z-[-1] before:h-57pxr before:w-full before:bg-theme-bg-base" onClickTab={(_, e) => LOG_CLICK_EVENT(`${gnbMenuUidMap[menuUid].text}_서브탭_클릭`, e)} />
        </TabPositionSticky>}
      {!layoutId && <div className="min-h-screen flex-center-full">
          <Loading />
        </div>}
      {layoutId && <PageLayout id={layoutId} className={twMerge(!isMobile && 'pt-32pxr')}></PageLayout>}
      {menuUid !== undefined && <TrackPageViewContainer trackObj={{
      shouldSendUsagePage: true,
      section: gnbMenuUidMap[menuUid].text,
      page: `${gnbMenuUidMap[menuUid].text}_${replaceWhiteSpaceToUnderbar(subtabTitle)}`,
      actionType: 'Pageview',
      actionName: `${gnbMenuUidMap[menuUid].text}_${replaceWhiteSpaceToUnderbar(subtabTitle)}_화면`,
      page_meta: {
        id: String(screenUid),
        type: 'screenId'
      },
      customProps: {
        user_action: 'pv',
        tab_type: 'section'
      }
    }} customPropsDataKeyList={[PageViewCustomPropsDataKey.PageCategoryFilter, PageViewCustomPropsDataKey.PageGenreFilter, PageViewCustomPropsDataKey.PageBmFilter, PageViewCustomPropsDataKey.PageDay, PageViewCustomPropsDataKey.PageSort, PageViewCustomPropsDataKey.Completed, PageViewCustomPropsDataKey.TabType]} />}
    </div>;
}
Menu.getInitialProps = nextReduxWrapper.getInitialPageProps<MenuProps>(store => async context => {
  const menu = await getMenuFromStore({
    store,
    req: context?.req as Request,
    res: context?.res as Response
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [menuUid, _, screenUid] = context.query?.pathParams as string[];
  const menuUidNum = Number(menuUid);
  let validSubtabMenu = menu.subtabMenuList.find((subtabMenu: SubtabMenu) => subtabMenu.uid === menuUidNum);
  if (!validSubtabMenu) {
    // 과거 웹툰 : 10, 웹소설 : 11 인 시절의 URL 로 들어온 경우 현재 메뉴인 10010, 10011 로 변합니다
    const legacyCompatibilityMenuUid = menuUidNum < 10000 ? menuUidNum + 10000 : undefined;
    validSubtabMenu = menu.subtabMenuList.find((subtabMenu: SubtabMenu) => subtabMenu.uid === legacyCompatibilityMenuUid);
    // 최종으로 서브탭메뉴를 찾을 수 없다면 추천홈(10000)으로 보여줍니다
    if (!validSubtabMenu) {
      redirectInInitialProps({
        destination: `/menu/${menu.subtabMenuList[0].uid}`,
        res: context.res
      });
      return {};
    }
  }
  const screenUidNum = Number(screenUid);
  const validScreen = screenUid ? validSubtabMenu?.screenList?.find((screen: Screen) => screen.uid === screenUidNum) : validSubtabMenu?.screenList[0]; // 스크린 번호가 없다면 서브탭메뉴 첫번째 스크린을 보여줍니다(지금 핫한)

  // 존재하지 않는 스크린 번호로 잘못 들어온 경우엔 스크린 번호 제거 후 서브탭메뉴 홈으로 리다이렉트 합니다.
  if (!validScreen) {
    redirectInInitialProps({
      destination: `/menu/${validSubtabMenu.uid}`,
      res: context.res
    });
    return {};
  }
  if (menu.error?.type === PagewebErrorType.NicolasApiStatusError) {
    store.dispatch(addError({
      error: menu.error
    }));
    return;
  }
  if (!validSubtabMenu || !validScreen) {
    return {
      notFound: true
    };
  }
  try {
    const {
      queryClient,
      ...result
    } = await handleMenuData({
      store,
      menu,
      menuUid: validSubtabMenu.uid,
      screenUid: validScreen.uid,
      ssrRequest: context?.req as Request,
      query: context.query,
      cookie: context?.req?.headers.cookie
    });
    return {
      ...result,
      ...(queryClient && {
        ...getDehydratedState(queryClient)
      })
    };
  } catch (error) {
    store.dispatch(addError({
      error: thunkSerializeError(error)
    }));
    return {};
  }
});
Menu.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={page.props.metaInfo?.title} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowGnb: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.MainAndSub
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }} metaInfo={page.props.metaInfo}>
      {page}
    </Layout>;
};
export default Menu;